import type { EntryPoint, ExtensionPointModule } from '@helpcenter/forge-ui-utils';
import { JIRA_MODULES } from '@helpcenter/forge-ui-utils/src/constants';
import {
	commonErrors,
	userConsentErrorRegex,
	KNOWN_NETWORK_ERRORS,
	KNOWN_NETWORK_ERRORS_TYPES,
	MODULE_TYPE_EXTENSION,
} from './constants';
import type { EventId, ModuleType } from './types';

export const getErrorType = (_error: unknown) => {
	if (_error == null) {
		return 'NULL_OR_UNDEFINED';
	}

	const error = _error as Error;

	const message = String(error instanceof Error ? error.message : error).trim();

	if (message === "A hidden extension shouldn't be rendered") {
		return 'HIDDEN_EXTENSION_RENDER_ATTEMPT';
	}

	const rateLimitingRegex = new RegExp(
		"Response ended with an error message: You are trying to access '.*' too often",
		'i',
	);
	if (rateLimitingRegex.test(message)) {
		return 'RATE_LIMITING';
	}

	const serverErrorRegex = new RegExp('^Error server response: (\\d+)$', 'i');
	if (serverErrorRegex.test(message)) {
		const match = message.match(serverErrorRegex);
		const statusCode = match ? match[1] : 'NO_STATUS_CODE';

		return `ERROR_SERVER_RESPONSE_${statusCode}`;
	}

	const aggGiraServerErrorRegex = new RegExp(
		'The underlying service gira status code is: (\\d+)$',
		'im',
	);
	if (aggGiraServerErrorRegex.test(message)) {
		const match = message.match(aggGiraServerErrorRegex);
		const statusCode = match?.[1] ?? 'NO_STATUS_CODE';

		return `AGG_GIRA_ERROR_${statusCode}`;
	}

	const dataClassification404ErrorRegex = new RegExp(
		'Data classification request failed: Error server response: 404',
		'i',
	);
	const dataClassification401ErrorRegex = new RegExp(
		'Data classification request failed: Error server response: 401',
		'i',
	);

	if (
		dataClassification401ErrorRegex.test(message) ||
		dataClassification404ErrorRegex.test(message)
	) {
		return 'DATA_CLASSIFICATION_NO_ACCESS';
	}

	// Add here errors related to a broken app configuration/manifest
	if (message.startsWith('Function definition is invalid.')) {
		return 'APP_DEV_ERROR';
	}

	const graphqlErrorRegex = new RegExp('^GraphQL error', 'i');
	if (graphqlErrorRegex.test(message)) {
		return 'GRAPHQL_ERROR';
	}

	if (userConsentErrorRegex.test(message)) {
		return 'USER_DIDNT_CONSENT_ERROR';
	}

	const aggUnderlyingServiceTimeoutRegex = new RegExp('The underlying service call timed out', 'i');
	if (aggUnderlyingServiceTimeoutRegex.test(message)) {
		return 'AGG_UNDERLYING_SERVICE_TIMEOUT';
	}

	const appValueFunction = new RegExp("The app's value function failed", 'i');
	const serverValueFunction408ErrorRegex = new RegExp(
		'Invoke value functions failed: Error server response: 408',
		'i',
	);
	const invokeValueFunctionFailed = new RegExp('Invoke value functions failed:', 'i');

	/**
	 * In case when we get bad request(400) from the BE it should be treated as an app error as there
	 * are many ways to fail the value function
	 */
	const valueFunctionAppsBadRequest =
		invokeValueFunctionFailed.test(message) && 'statusCode' in error && error.statusCode === 400;

	/**
	 * In case when we get request timeout(408) from the XIS invocation it should be treated as an app error.
	 * Error occurs when invocation exceeds 25s threshold e.g.too long time to execute request to external API
	 */

	const valueFunctionTimeoutError =
		serverValueFunction408ErrorRegex.test(message) &&
		'statusCode' in error &&
		error.statusCode === 408;

	if (appValueFunction.test(message) || valueFunctionTimeoutError || valueFunctionAppsBadRequest) {
		return 'VALUE_FUNCTION_APP_ERROR';
	}

	/*
	 * 	Whole message:
	 *	Fetching formatted value after edit failed: FetchError: Fetch call failed with status code: 429
	 */
	const valueFunctionEditRateLimitedRegex = new RegExp(
		'Fetch call failed with status code: 429',
		'i',
	);
	/*
	 * 	Whole message:
	 *	Invoke value functions failed: Error server response: 429
	 */
	const valueFunctionInvokeRateLimitedRegex = new RegExp('Error server response: 429', 'i');

	if (
		valueFunctionEditRateLimitedRegex.test(message) ||
		valueFunctionInvokeRateLimitedRegex.test(message)
	) {
		return 'RATE_LIMITING';
	}

	// The full message states:
	// Extension "extension name" does not exist, the app may have changed or no longer be installed in this context
	const extensionDoesntExistRegex = new RegExp('^Extension "(.*)" does not exist', 'i');
	if (extensionDoesntExistRegex.test(message)) {
		return 'EXTENSION_DOESNT_EXIST';
	}

	// In case of React error message:
	// Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
	const removeChildOnNodeRegex = new RegExp("^Failed to execute 'removeChild' on 'Node'", 'i');

	if (removeChildOnNodeRegex.test(message)) {
		return 'REMOVE_CHILD_ERROR';
	}

	if (isChunkLoadError(error as ErrorWithCause)) {
		return 'CHUNK_LOAD';
	}

	const entry = Object.entries(commonErrors).find(
		(arrayItem) => String(arrayItem[1]).toLowerCase() === message.toLowerCase(),
	);
	if (entry) {
		return entry[0];
	}

	if (isClientFetchError(error)) {
		return 'FAILED_TO_FETCH';
	}

	if (error != null && 'statusCode' in error && error.statusCode === 429) {
		return 'RATE_LIMITING';
	}

	return 'OTHER';
};

interface ErrorWithCause extends Error {
	cause?: {
		name: string;
		message: string;
	};
}

export const isClientFetchError = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') {
		return false;
	}

	const err = error as ErrorWithCause;
	return (
		KNOWN_NETWORK_ERRORS_TYPES.includes(err.name) ||
		isClientFetchErrorMessage(err.message) ||
		(typeof err.cause === 'object' &&
			(KNOWN_NETWORK_ERRORS_TYPES.includes(err.cause.name) ||
				isClientFetchErrorMessage(err.cause.message)))
	);
};

export const isChunkLoadError = (error: ErrorWithCause) => {
	// React loosely lazy chunk load error type
	if (typeof error === 'object' && error.name === 'ChunkLoadError') {
		return true;
	}

	// Other chunk load error types
	// In theory it should be handled by a common isClientFetchError, but it ignores TypeError error types
	const KNOWN_CHUNK_LOAD_ERRORS = [
		'Failed to fetch dynamically imported module',
		'error loading dynamically imported module',
		'Importing a module script failed',
		'Cannot find module',
	];

	const isChunkLoadErrorMessage = (message: string): boolean =>
		Boolean(message) &&
		!!KNOWN_CHUNK_LOAD_ERRORS.find((networkErrorMessage) =>
			// Using `includes` here because the underlying fetch error messages
			// are sometimes wrapped to provide additional context, e.g.
			// "Something went wrong: ${error.message}"
			message.includes(networkErrorMessage),
		);

	return (
		Boolean(error) &&
		typeof error === 'object' &&
		(isChunkLoadErrorMessage(error.message) ||
			(typeof error.cause === 'object' &&
				error.cause &&
				'message' in error.cause &&
				isChunkLoadErrorMessage(String(error.cause.message))))
	);
};

const isClientFetchErrorMessage = (message: string): boolean =>
	Boolean(message) &&
	KNOWN_NETWORK_ERRORS.some((networkErrorMessage: string) =>
		// Using `includes` here because the underlying fetch error messages
		// are sometimes wrapped to provide additional context, e.g.
		// "Something went wrong: ${error.message}"
		message.includes(networkErrorMessage),
	);

interface ModuleParams {
	module: ExtensionPointModule;
	moduleType?: ModuleType;
	entryPoint?: EntryPoint;
}

const capitalizeFirstLetter = (str?: string) =>
	(str != null && str.charAt(0).toUpperCase() + str.slice(1)) || '';

const prepareModuleMode = (entryPoint?: EntryPoint): string =>
	`${capitalizeFirstLetter(entryPoint)}`;

export const normalizeModuleName = (moduleName: ExtensionPointModule) =>
	moduleName.replace(/^jira:/, '');

export const prepareModuleName = (moduleName: ExtensionPointModule) =>
	JIRA_MODULES.includes(moduleName) ? normalizeModuleName(moduleName) : 'unknown';

export const getEventId = ({
	entryPoint,
	module,
	moduleType = MODULE_TYPE_EXTENSION,
}: ModuleParams): EventId => {
	const eventNamePostfix = moduleType === 'trigger' ? 'Trigger' : '';
	// we use view entry point for custom field and custom field type but we don't want to update all the event names
	if (
		entryPoint === 'view' &&
		(module === 'jira:customField' || module === 'jira:customFieldType')
	) {
		return `${prepareModuleName(module)}${eventNamePostfix}`;
	}
	return `${prepareModuleName(module)}${prepareModuleMode(entryPoint)}${eventNamePostfix}`;
};
